import React from 'react';
import { useTranslation } from 'react-i18next';

interface GalleryImage {
  src: string;
  alt: string;
}

const HeroGallery: React.FC = () => {
  const { t } = useTranslation();

  // First carousel - moves left (7 images)
  const topImages: GalleryImage[] = [
    { src: '/restaurant_photos/photo1.webp', alt: 'Elegant dining room with luxury decor' },
    { src: '/restaurant_photos/photo2.webp', alt: 'Buffet area with extensive food selection' },
    { src: '/restaurant_photos/photo3.webp', alt: 'Modern buffet setup with elegant lighting' },
    { src: '/restaurant_photos/photo4.webp', alt: 'Premium buffet display with gourmet options' },
    { src: '/restaurant_photos/photo5.webp', alt: 'Fresh dessert and fruit station' },
    { src: '/restaurant_photos/photo6.webp', alt: 'Sushi and Japanese cuisine section' },
    { src: '/restaurant_photos/photo7.webp', alt: 'International buffet with diverse options' },
  ];

  // Second carousel - moves right (6 images)
  const bottomImages: GalleryImage[] = [
    { src: '/restaurant_photos/photo8.webp', alt: 'Fresh sashimi and seafood display' },
    { src: '/restaurant_photos/photo9.webp', alt: 'Gourmet dishes and specialties' },
    { src: '/restaurant_photos/photo10.webp', alt: 'Premium seafood selection' },
    { src: '/restaurant_photos/photo11.webp', alt: 'Elegant restaurant interior' },
    { src: '/restaurant_photos/photo12.webp', alt: 'Fine dining experience' },
    { src: '/restaurant_photos/photo13.webp', alt: 'Exquisite food presentation' },
  ];

  return (
    <section className="py-12 md:py-20 lg:py-32 bg-luxury-cream relative">
      {/* Top fade transition from white */}
      <div className="absolute top-0 left-0 right-0 h-16 bg-gradient-to-b from-white to-transparent" />
      
      <div className="max-w-full relative z-10">
        <div className="section-padding mb-8 md:mb-12 lg:mb-20 relative">
          <div className="text-center">
            <h2 className="text-display-md md:text-display-lg font-display mb-6 text-luxury-black">
              {t('gallery.title', 'Découvrez Notre Restaurant')}
            </h2>
            <div className="h-px bg-luxury-gold mx-auto w-20" />
          </div>
          {/* Absolute positioned fish decorations */}
          <div className="absolute left-12 top-1/2 transform -translate-y-1/2 hidden lg:block opacity-30">
            <img 
              src="/fish-decoration.png" 
              alt="Fish decoration" 
              className="w-48 object-contain"
            />
          </div>
          <div className="absolute right-12 top-1/2 transform -translate-y-1/2 hidden lg:block opacity-30">
            <img 
              src="/fish-decoration.png" 
              alt="Fish decoration" 
              className="w-48 object-contain transform scale-x-[-1]"
            />
          </div>
        </div>

        {/* First Carousel - Scrolls Left */}
        <div className="relative mb-8">
          <div className="flex animate-scroll-left-mobile md:animate-scroll-left">
            {/* Triple the images for seamless loop */}
            {[...topImages, ...topImages, ...topImages].map((image, index) => (
              <div
                key={index}
                className="flex-shrink-0 w-[400px] h-[300px] mx-4"
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full rounded-lg shadow-lg"
                  style={{
                    objectFit: 'cover',
                    objectPosition: '50% 40%',
                    imageRendering: 'auto'
                  }}
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Second Carousel - Scrolls Right */}
        <div className="relative">
          <div className="flex animate-scroll-right-mobile md:animate-scroll-right">
            {/* Triple the images for seamless loop */}
            {[...bottomImages, ...bottomImages, ...bottomImages].map((image, index) => (
              <div
                key={index}
                className="flex-shrink-0 w-[400px] h-[300px] mx-4"
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full rounded-lg shadow-lg"
                  style={{
                    objectFit: 'cover',
                    objectPosition: '50% 40%',
                    imageRendering: 'auto'
                  }}
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mt-8 section-padding">
          <a href="/gallery">
            <button className="luxury-button">
              {t('gallery.viewMore', 'Voir Plus de Photos')}
            </button>
          </a>
        </div>
      </div>
    </section>
  );
};

export default HeroGallery;